import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, toneForHealth } from '../lib/format';

interface Credential {
  id: string;
  version: number;
  keyVersion: string;
  fingerprint: string;
  authScheme: string;
  isActive: boolean;
  createdAt: string;
  rotatedAt: string | null;
  masked: string;
}

interface Account {
  id: string;
  providerId: string;
  providerKey: string;
  providerType: string;
  label: string;
  environment: string;
  baseUrl: string;
  state: string;
  healthState: string;
  lastHealthCheckAt: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastErrorReason: string | null;
  isInternalService: boolean;
  useProxy: boolean;
  allowedHosts: string[];
  targetCount: number;
  monthlyBudgetUsd: string | null;
  budgetAction: string;
  credentials: Credential[];
}

const EMPTY_FORM = {
  providerId: '',
  label: '',
  environment: 'test',
  baseUrl: '',
  secret: '',
  authScheme: 'bearer',
  headerName: '',
  isInternalService: false,
  useProxy: false,
  allowedHosts: '',
};

export function ConnectionsPage({ role }: { role: string }) {
  const accounts = useResource<{ items: Account[] }>('/api/admin/provider-accounts');
  const providers = useResource<{ items: Array<{ id: string; key: string; type: string }> }>(
    '/api/admin/providers',
  );
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [rotating, setRotating] = useState<Account | null>(null);
  const [rotateSecret, setRotateSecret] = useState('');
  const [internalConfirm, setInternalConfirm] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  const canWrite = role === 'OWNER' || role === 'ADMIN';
  const canOperate = canWrite || role === 'OPERATOR';

  const create = async () => {
    try {
      await api.post('/api/admin/provider-accounts', {
        providerId: form.providerId,
        label: form.label,
        environment: form.environment,
        baseUrl: form.baseUrl,
        secret: form.secret,
        authScheme: form.authScheme,
        authConfig:
          form.authScheme === 'header' && form.headerName ? { headerName: form.headerName } : {},
        isInternalService: form.isInternalService,
        useProxy: form.useProxy,
        allowedHosts: form.allowedHosts
          .split(',')
          .map((host) => host.trim())
          .filter(Boolean),
      });
      toast.success(
        'Połączenie zapisane',
        'Sekret został zaszyfrowany i nie może zostać ponownie wyświetlony.',
      );
      setOpen(false);
      setForm(EMPTY_FORM);
      accounts.reload();
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    }
  };

  const test = async (account: Account) => {
    setTesting(account.id);
    try {
      const result = await api.post<{
        ok: boolean;
        message: string;
        health: string;
        latencyMs: number;
      }>(`/api/admin/provider-accounts/${account.id}/test`);
      if (result.ok) toast.success(`Test OK (${result.latencyMs} ms)`, result.message);
      else toast.error(`Test nieudany — ${result.health}`, result.message);
      accounts.reload();
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    } finally {
      setTesting(null);
    }
  };

  const setState = async (account: Account, state: string) => {
    try {
      await api.patch(`/api/admin/provider-accounts/${account.id}`, { state });
      accounts.reload();
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    }
  };

  return (
    <>
      <PageHeader
        title="Połączenia"
        description="Konta dostawców i ich zaszyfrowane poświadczenia. Po zapisaniu sekret jest widoczny wyłącznie jako odcisk palca."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Dodaj połączenie
            </Button>
          ) : undefined
        }
      />

      <Card tight>
        {accounts.loading ? (
          <div className="ah-card-body">
            <Skeleton rows={5} />
          </div>
        ) : (
          <DataTable
            rows={accounts.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak połączeń"
                description="Dodaj konto dostawcy, aby Hub mógł wywoływać upstream w Twoim imieniu."
              />
            }
            columns={[
              {
                key: 'account',
                header: 'Konto',
                render: (row) => (
                  <>
                    <span className="ah-mono">
                      {row.providerKey}/{row.label}
                    </span>
                    <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                      {row.baseUrl}
                    </div>
                    {row.isInternalService && (
                      <div style={{ marginTop: 3 }}>
                        <Badge tone="warn">sieć wewnętrzna</Badge>
                      </div>
                    )}
                    {row.useProxy && (
                      <div style={{ marginTop: 3 }}>
                        <Badge tone="neutral">przez proxy</Badge>
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'env',
                header: 'Środowisko',
                render: (row) => (
                  <Badge tone={row.environment === 'production' ? 'danger' : 'neutral'}>
                    {row.environment}
                  </Badge>
                ),
              },
              {
                key: 'state',
                header: 'Stan',
                render: (row) => (
                  <>
                    <Badge
                      tone={
                        row.state === 'ENABLED'
                          ? 'ok'
                          : row.state === 'DRAINING'
                            ? 'warn'
                            : 'neutral'
                      }
                    >
                      {row.state}
                    </Badge>
                    <div style={{ marginTop: 3 }}>
                      <Badge tone={toneForHealth(row.healthState)}>{row.healthState}</Badge>
                    </div>
                  </>
                ),
              },
              {
                key: 'credential',
                header: 'Poświadczenie',
                render: (row) => {
                  const active =
                    row.credentials.find((credential) => credential.isActive) ?? row.credentials[0];
                  if (!active) return <Badge tone="danger">brak</Badge>;
                  return (
                    <>
                      <div className="ah-mono">{active.masked}</div>
                      <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                        v{active.version} · {active.authScheme} · klucz {active.keyVersion}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                        odcisk {active.fingerprint}
                      </div>
                    </>
                  );
                },
              },
              {
                key: 'health',
                header: 'Ostatnie zdarzenia',
                render: (row) => (
                  <div style={{ fontSize: 12 }}>
                    <div>test: {formatDateTime(row.lastHealthCheckAt)}</div>
                    <div>sukces: {formatDateTime(row.lastSuccessAt)}</div>
                    {row.lastErrorReason && (
                      <div style={{ color: 'var(--ah-danger)' }} title={row.lastErrorReason}>
                        {row.lastErrorReason.slice(0, 48)}…
                      </div>
                    )}
                  </div>
                ),
              },
              { key: 'targets', header: 'Cele', align: 'right', render: (row) => row.targetCount },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <div
                    style={{
                      display: 'flex',
                      gap: 4,
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap',
                    }}
                  >
                    {canOperate && (
                      <Button size="sm" disabled={testing === row.id} onClick={() => test(row)}>
                        {testing === row.id ? 'Testuję…' : 'Testuj'}
                      </Button>
                    )}
                    {canOperate && row.state === 'ENABLED' && (
                      <Button size="sm" onClick={() => setState(row, 'DRAINING')}>
                        Wygaszaj
                      </Button>
                    )}
                    {canOperate && row.state !== 'ENABLED' && (
                      <Button size="sm" onClick={() => setState(row, 'ENABLED')}>
                        Włącz
                      </Button>
                    )}
                    {canWrite && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setRotating(row);
                          setRotateSecret('');
                        }}
                      >
                        Rotuj sekret
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowe połączenie"
        description="Sekret podajesz raz. Zostanie zaszyfrowany kluczem głównym i nigdy nie będzie zwrócony przez API."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              onClick={create}
              disabled={!form.providerId || !form.label || !form.baseUrl || !form.secret}
            >
              Zapisz
            </Button>
          </>
        }
      >
        <Field label="Dostawca">
          {(id) => (
            <Select
              id={id}
              value={form.providerId}
              onChange={(event) => setForm({ ...form, providerId: event.target.value })}
            >
              <option value="">— wybierz —</option>
              {(providers.data?.items ?? []).map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.key} ({provider.type})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Etykieta" hint="Nazwa widoczna tylko dla administratorów, np. „konto-a”.">
          {(id) => (
            <TextInput
              id={id}
              value={form.label}
              onChange={(event) => setForm({ ...form, label: event.target.value })}
            />
          )}
        </Field>
        <Field label="Środowisko">
          {(id) => (
            <Select
              id={id}
              value={form.environment}
              onChange={(event) => setForm({ ...form, environment: event.target.value })}
            >
              <option value="test">test</option>
              <option value="staging">staging</option>
              <option value="production">production</option>
            </Select>
          )}
        </Field>
        <Field
          label="Bazowy adres URL"
          hint="Wymagany HTTPS, chyba że to allowlistowana usługa wewnętrzna."
        >
          {(id) => (
            <TextInput
              id={id}
              value={form.baseUrl}
              onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
              placeholder="https://api.openai.com"
            />
          )}
        </Field>
        <Field label="Schemat uwierzytelniania">
          {(id) => (
            <Select
              id={id}
              value={form.authScheme}
              onChange={(event) => setForm({ ...form, authScheme: event.target.value })}
            >
              <option value="bearer">Bearer (Authorization)</option>
              <option value="header">Własny nagłówek</option>
              <option value="basic">Basic</option>
              <option value="query">Parametr zapytania</option>
              <option value="none">Brak</option>
            </Select>
          )}
        </Field>
        {form.authScheme === 'header' && (
          <Field label="Nazwa nagłówka">
            {(id) => (
              <TextInput
                id={id}
                value={form.headerName}
                onChange={(event) => setForm({ ...form, headerName: event.target.value })}
                placeholder="x-api-key"
              />
            )}
          </Field>
        )}
        <Field
          label="Sekret"
          hint="Wpisywany jednokrotnie. Formularz nigdy nie jest wypełniany wartością z serwera."
        >
          {(id) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="new-password"
              value={form.secret}
              onChange={(event) => setForm({ ...form, secret: event.target.value })}
            />
          )}
        </Field>

        <Checkbox
          label="To jest usługa wewnętrzna (adres w sieci prywatnej)"
          hint="Wymaga roli OWNER oraz jawnej listy dozwolonych hostów. Adresy metadanych chmury pozostają zablokowane zawsze."
          checked={form.isInternalService}
          onChange={(checked) => {
            if (checked) setInternalConfirm(true);
            else setForm({ ...form, isInternalService: false });
          }}
        />
        <Checkbox
          label="Kieruj ruch przez proxy wychodzące"
          hint="Dla upstreamów limitujących po adresie IP, a nie po kluczu (np. GUS BDL). Wymaga skonfigurowanego WEBSHARE_PROXY_URL. Niedostępne dla usług wewnętrznych."
          checked={form.useProxy}
          disabled={form.isInternalService}
          onChange={(checked) => setForm({ ...form, useProxy: checked })}
        />

        {form.isInternalService && (
          <Field
            label="Dozwolone hosty"
            hint="Lista rozdzielona przecinkami. Puste = brak dostępu."
          >
            {(id) => (
              <TextInput
                id={id}
                value={form.allowedHosts}
                onChange={(event) => setForm({ ...form, allowedHosts: event.target.value })}
                placeholder="internal-api.example.local"
              />
            )}
          </Field>
        )}
      </Modal>

      <ConfirmDialog
        open={internalConfirm}
        onOpenChange={setInternalConfirm}
        title="Włączyć dostęp do sieci wewnętrznej?"
        description={
          <>
            Konto oznaczone jako wewnętrzne może kierować ruch do adresów prywatnych. Wymaga to
            jawnej listy hostów. Punkty końcowe metadanych chmury pozostają zablokowane niezależnie
            od konfiguracji. Akcja jest audytowana.
          </>
        }
        confirmLabel="Włącz"
        onConfirm={() =>
          setForm((current) => ({ ...current, isInternalService: true, useProxy: false }))
        }
      />

      <Modal
        open={rotating !== null}
        onOpenChange={(value) => !value && setRotating(null)}
        title="Rotacja poświadczenia"
        description={
          rotating
            ? `${rotating.providerKey}/${rotating.label}. Poprzednia wersja pozostaje odszyfrowywalna, ale nowe żądania użyją nowego sekretu.`
            : ''
        }
        footer={
          <>
            <Button onClick={() => setRotating(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={rotateSecret.length === 0}
              onClick={async () => {
                if (!rotating) return;
                try {
                  await api.post(`/api/admin/provider-accounts/${rotating.id}/credentials`, {
                    secret: rotateSecret,
                    authScheme:
                      rotating.credentials.find((credential) => credential.isActive)?.authScheme ??
                      'bearer',
                  });
                  toast.success('Poświadczenie zrotowane');
                  setRotating(null);
                  setRotateSecret('');
                  accounts.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Zapisz nową wersję
            </Button>
          </>
        }
      >
        <Callout>
          Rotacja bez przestoju: nowa wersja staje się aktywna natychmiast, a stara jest oznaczana
          jako nieaktywna i pozostaje możliwa do odszyfrowania na potrzeby audytu.
        </Callout>
        <Field label="Nowy sekret">
          {(id) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="new-password"
              value={rotateSecret}
              onChange={(event) => setRotateSecret(event.target.value)}
            />
          )}
        </Field>
      </Modal>
    </>
  );
}
